import type { FC } from 'react';
import { useCallback } from 'react';

import { useGetChat } from '@/features/api/hooks/communication-chat/useGetChat';
import { useSendChatMessage } from '@/features/api/hooks/communication-chat-events/useSendChatMessage';
import { BlockedChatState } from '@/features/chats/components/BlockedChatState';
import { ChatLayout } from '@/features/chats/components/ChatLayout';
import { ChatMessageEditor } from '@/features/chats/components/ChatMessageEditor';
import { ChatMessages } from '@/features/chats/components/ChatMessages';
import { ChatNavigationBar } from '@/features/chats/components/ChatNavigationBar';
import { useChatPartner } from '@/features/chats/hooks/useChatPartner';

type Props = {
  chatId: string;
  autofocus?: boolean;
};

export const ExistingChat: FC<Props> = ({ chatId, autofocus = false }) => {
  const { data: chat } = useGetChat(chatId);
  const { mutateAsync: sendChatMessage } = useSendChatMessage(chatId);
  const partner = useChatPartner(chat);

  const handleSendMessage = useCallback(
    async (message: string, temporaryMediaItemIds: string[]) =>
      sendChatMessage({ message, temporaryMediaItemIds, created: Date.now() }),
    [sendChatMessage]
  );

  const deleted = partner?.deleted ?? false;
  const blocked = partner?.filterStatus === 'BLOCKED';

  return (
    <ChatLayout
      header={
        <ChatNavigationBar
          chatPartner={partner}
          chatId={chatId}
          archived={chat?.archived}
        />
      }
      messages={
        blocked ? (
          <BlockedChatState />
        ) : (
          <ChatMessages chatId={chatId} deleted={deleted} />
        )
      }
      editor={
        blocked || deleted ? undefined : (
          <ChatMessageEditor
            onSendChatMessage={handleSendMessage}
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autofocus}
          />
        )
      }
      deleted={deleted}
    />
  );
};
